"use client";

import { levelForProduct } from "@/lib/groupBrandDiscount";
import { useWaTemplates } from "@/lib/supabase/waTemplates";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  ArrowLeft,
  Camera,
  MapPin,
  MessageCircle,
  Share2,
  ShoppingCart,
  Store,
  Tag,
  UsersRound,
  Warehouse,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CartCheckout } from "@/components/CartCheckout";
import { showGuestWaOrder } from "@/lib/guestWaOrder";
import {
  partnerDirectoryPath,
  showPartnerProductCta,
} from "@/lib/partnerDirectory";
import {
  ProductImageGallery,
  type GalleryEdge,
  type ProductGalleryControl,
} from "@/components/ProductImageGallery";
import { NextProductPeek } from "@/components/NextProductPeek";
import { NotificationBell } from "@/components/NotificationBell";
import { SaveProductButton } from "@/components/SaveProductButton";
import {
  EMPTY_PRODUCT_FEED,
  productFeedHref,
  type ProductFeed,
} from "@/lib/productFeed";
import { markFeedHintSeen, readFeedHintSeen } from "@/lib/productFeedHint";
import { prefetchFull } from "@/lib/nextPrefetch";
import { topLoader } from "@/lib/topLoader";
import { TelegramIcon } from "@/components/icons/TelegramIcon";
import { QuantityStepper } from "@/components/QuantityStepper";
import { SizeGuideButton } from "@/components/SizeGuideButton";
import { useCatalogCart } from "@/components/toko/catalogCartContext";
import { StatusBadge } from "@/components/StatusBadge";
import { StoreImage } from "@/components/StoreImage";
import { VariantGroupSelector } from "@/components/VariantGroupSelector";
import {
  type Product,
  type ProductVariation,
  isOrderableStatus,
  isStockTracked,
  isVariationInStock,
  isVariationAvailable,
  type VariantShift,
  describeVariantShift,
  firstInStockVariation,
} from "@/lib/products";
import {
  quotaRemaining,
  quotaStorefrontLine,
  type PoQuotaTone,
} from "@/lib/poQuota";
import { normalizeBrandIconUrl, normalizeBrandTelegramUrl } from "@/lib/brands";
import { buildProductGallery } from "@/lib/productGallery";
import {
  deriveVariantLabel,
  findVariationByOptionIds,
  firstSelectableSelection,
  isComboBlocked,
  isSelectionComplete,
  resolveSelectionForOption,
  optionSelectionKey,
  buildSelectionSearch,
  pricingSelection,
  shouldUseInheritedPricing,
  type PreorderVariantFailure,
} from "@/lib/lazyVariants";
import { pickerBlockedSet } from "@/lib/comboListMode";
import {
  fetchPreorderQuotaUsed,
  fetchProductStockSnapshot,
  resolvePublicInheritedPricing,
  resolvePublicVariant,
  type ProductStockSnapshot,
} from "@/lib/supabase/public-data";
import { resolveOrCreatePreorderVariant } from "@/lib/supabase/preorderVariant";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { canGoBackInApp, isPlainClick } from "@/lib/historyNav";
import {
  discountBadgeLabel,
  formatPrice,
  getProductPrice,
  getPriceBadgeLabel,
  getDpBadgeLabel,
  guestSellablePrice,
  resolveSelectionPrice,
  sellableSelectionPrice,
} from "@/lib/pricing";
import {
  effectivePriceEstimate,
  PRICE_ESTIMATE_NOTE,
  priceEstimateLabel,
  variantPriceEstimate,
} from "@/lib/priceEstimate";
import {
  discountCutLabel,
  promoRemainingLabel,
  promoTimeLeftLabel,
  promosForLevel
} from "@/lib/promo";
import {
  useGuestPromosFor,
  usePromoNow,
  usePromoRemaining,
  usePromosFor
} from "@/context/PromoContext";
import { buildAddToCartPayload, buildViewContentPayload } from "@/lib/pixel";
import { firePixelEvent } from "@/lib/pixelClient";
import { RECENT_VIEW_DWELL_MS, recordRecentView } from "@/lib/recentlyViewed";
import {
  buildProductShareText,
  buildProductShareUrl,
  buildWhatsAppShareLink,
  sharePhotoToStatus,
} from "@/lib/shareProduct";
import {
  productShareNeedsStock,
  sharePriceLabel,
  shareStockLines,
} from "@/lib/productShareMessage";
import {
  buildCatalogKategoriPath,
  buildCatalogMerekPath,
  buildCatalogOrderMessage,
  buildCatalogOrderWaUrl,
  buildCatalogPath,
  buildCatalogProductUrl,
  catalogShareOrigin,
  displayResellerName,
} from "@/lib/resellerCatalog";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { priceExtraNote, priceExtraSubject } from "@/lib/priceExtraLabel";
import { useCart } from "@/lib/cart";
import { useCustomer, useCustomerGroup } from "@/context/CustomerContext";
import {
  useAdminWhatsapp,
  useGuestWaOrder,
  usePartnerProductCta,
  useStoreName,
} from "@/lib/supabase/siteSettings";
import { fetchActivePrograms } from "@/lib/supabase/loyalty";
import { productPointsHint, programsForProduct } from "@/lib/loyalty";
import { BundleCard } from "@/components/bundle/BundleCard";
import { ProductGiftBlock } from "@/components/ProductGiftBlock";
import { bundlesForBuyer, type BundleCardData } from "@/lib/bundleStorefront";

/**
 * Mode Katalog Reseller (mig 216, halaman `/toko/[kode]/product/[slug]`):
 * harga SELALU normal (diskon reseller tak bocor), tanpa keranjang/checkout —
 * tombol Order membuka WhatsApp reseller; navigasi & share tetap di katalog.
 */
export type ProductDetailCatalogMode = {
  kode: string;
  resellerName: string;
  /** Nomor WA reseller sudah dinormalkan 62xxx (toWaMeNumber di halaman server). */
  waNumber: string;
};

/**
 * Warna baris kuota PO per nada. `ok` memakai `text-leaf` — warna yang SAMA
 * dengan baris "Stok tersedia" ready stock, karena bagi pembeli keduanya
 * menjawab satu pertanyaan yang sama: berapa yang masih bisa saya ambil.
 */
const QUOTA_TONE_CLASS: Record<PoQuotaTone, string> = {
  ok: "text-leaf",
  low: "text-marigold",
  out: "text-shopee",
};

/**
 * Pengaman "Membuka…" feed geser — cermin `NAV_SAFETY_MS` TopLoader: navigasi
 * yang tak pernah mendarat (jaringan putus) tak boleh mengunci galeri selamanya.
 */
const FEED_OPEN_SAFETY_MS = 12_000;

export function ProductDetail({
  product,
  catalogMode,
  feed,
  initialSelection,
  warehouseName,
  bundles,
}: {
  product: Product;
  catalogMode?: ProductDetailCatalogMode;
  /**
   * Produk berikutnya/sebelumnya untuk feed geser galeri — PROMISE dari
   * halaman server (`getProductFeed`, sengaja tak di-await supaya HTML produk
   * tak menunggu). Tak diisi → galeri persis seperti dulu.
   */
  feed?: Promise<ProductFeed>;
  /**
   * Pilihan dari URL `?v=<opsi,…>&qty=…`, diurai halaman SERVER
   * (`parseSelectionQuery`, fail-safe). Dipakai sebagai state AWAL untuk
   * SEMUA produk (PR-IG-3): tautan item feed Katalog Instagram dan
   * `offers.url` JSON-LD menunjuk varian tertentu, dan HTML awal harus sudah
   * menampilkan varian — dan harga — itu. `optionIds` null / tak cocok satu
   * varian pun → pilihan bawaan seperti biasa.
   */
  initialSelection?: { optionIds: string[] | null; qty: number | null };
  /**
   * Nama gudang asal produk ini (Multigudang, mig 7260) — diisi HANYA halaman
   * produk toko (`/product/[slug]`) dan hanya saat saklar Multigudang toko
   * menyala. Katalog reseller tak pernah mengisinya (nama gudang toko induk tak
   * boleh tampil di white-label); `catalogMode` tetap menolaknya sebagai lapis
   * kedua. Kosong = tanpa badge, halaman persis seperti sebelumnya.
   */
  warehouseName?: string | null;
  /**
   * Bundel BERJALAN & TERSEDIA yang memuat produk ini (mig 8040) — diisi HANYA
   * halaman produk toko (`loadBundleCardsServer`). Katalog reseller tak pernah
   * mengisinya (harga tamu & white-label), dan `catalogMode` tetap menolaknya
   * sebagai lapis kedua. Kelayakan level dinilai di sini (`bundlesForBuyer`).
   */
  bundles?: BundleCardData[];
}) {
  const { addItem, totalQty } = useCart();
  // Keranjang KATALOG (mig 216) — `null` di toko utama, tempat provider-nya
  // tak dipasang. Hook tetap unconditional; yang menggerbangi pemakaiannya
  // adalah `catalogMode` di bawah.
  const catalogCart = useCatalogCart();
  const waTemplates = useWaTemplates();
  const pathname = usePathname();
  const router = useRouter();
  // Hooks tetap unconditional; mode katalog memaksa group=null agar pengunjung
  // yang kebetulan reseller login TIDAK melihat harga diskonnya di katalog.
  const rawGroup = useCustomerGroup();
  // Diskon merek per level (mig 7070) diselesaikan untuk produk INI — satu grup
  // untuk seluruh harga halaman ini. Kartu tetangga (NextProductPeek)
  // menyelesaikannya ulang untuk produknya sendiri (`getProductPriceView`).
  const group = useMemo(
    () => (catalogMode ? null : levelForProduct(rawGroup, product)),
    [catalogMode, rawGroup, product]
  );
  // Blok "Lebih hemat dalam bundel": hanya bundel yang berhak bagi level
  // pembeli ini; tak pernah di katalog reseller.
  const myBundles = useMemo(
    () => (catalogMode || !bundles?.length ? [] : bundlesForBuyer(bundles, rawGroup)),
    [catalogMode, bundles, rawGroup]
  );
  const storeName = useStoreName();
  const partnerCtaEnabled = usePartnerProductCta();
  // #1 Bagikan versi Katalog Saya: di toko UTAMA (bukan mode katalog), reseller
  // yang login dapat membagikan link produk versi KATALOG-nya sendiri sehingga
  // pembeli akhir order ke dia (bukan ke toko utama). Slug diambil query kecil
  // terpisah (RLS owner), SENGAJA tidak lewat CustomerContext (pola AccountClient).
  const { customer, isLoading: customerLoading, isAuthenticated } = useCustomer();
  // Nomor tujuan tombol "Order via WhatsApp" pengunjung TAMU = setelan
  // "Hubungi Admin" (`site_settings.whatsapp_number`, Pengaturan Website) —
  // nomor yang sama dengan tombol Hubungi Admin di seluruh website.
  const adminWhatsapp = useAdminWhatsapp();
  // Saklar pemilik toko untuk tombol itu (`guest_wa_order_enabled`, Pengaturan
  // Website → kartu Tombol WhatsApp Admin, satu kartu dengan nomor tujuannya).
  const guestWaOrderEnabled = useGuestWaOrder();
  const [myCatalogSlug, setMyCatalogSlug] = useState<string | null>(null);
  useEffect(() => {
    if (catalogMode || !customer || customer.status !== "active") {
      setMyCatalogSlug(null);
      return;
    }
    let alive = true;
    const db = createBrowserSupabase();
    if (!db) return;
    db.from("customers")
      .select("catalog_slug")
      .eq("id", customer.id)
      .maybeSingle()
      .then(({ data }) => {
        if (alive) setMyCatalogSlug((data?.catalog_slug as string | null) ?? null);
      });
    return () => {
      alive = false;
    };
  }, [catalogMode, customer?.id, customer?.status]);
  const cartRef = useRef<HTMLDivElement>(null);
  const [activeImage, setActiveImage] = useState(0);
  // Slider foto utama = foto produk + foto per-opsi variasi (`productGallery.ts`).
  // Foto opsi selama ini cuma jadi thumbnail 20×20 di dalam tombol variasi,
  // padahal file-nya berukuran penuh — pembeli tak pernah bisa melihat warna
  // yang ia pilih dengan jelas. Cover produk tetap slide 0.
  const gallery = useMemo(
    () => buildProductGallery(product.images, product.variantGroups),
    [product.images, product.variantGroups]
  );
  const galleryControl = useRef<ProductGalleryControl | null>(null);
  // Ketuk opsi ber-foto → slider melompat ke foto opsi itu. Opsi tanpa foto
  // (mis. grup Ukuran) TIDAK menggeser slider: `undefined` dari peta berarti
  // "tak ada fotonya", bukan "slide 0" — pembeli yang sedang melihat detail
  // bahan di slide 3 tak dilempar balik ke cover hanya karena memilih ukuran.
  // Halaman TIDAK ikut digulir naik ke galeri (keputusan pemilik 2026-10-05):
  // pembeli tetap di tombol variasi, dan fotonya sudah berganti saat ia
  // menggulir ke atas sendiri.
  const focusOptionPhoto = useCallback(
    (optionId: string) => {
      const index = gallery.optionImageIndex[optionId];
      if (index === undefined) return;
      galleryControl.current?.goTo(index);
    },
    [gallery]
  );

  // ─── FEED GESER (productFeed.ts) ───────────────────────────────────────────
  // Lewat foto terakhir → produk berikutnya (sekategori & setipe, terbaru
  // dulu; kategori habis → kategori berikutnya); lewat foto pertama → produk
  // sebelumnya. Datanya promise dari server: dibaca di EFFECT, bukan `use()` —
  // menukar galeri saat promise selesai akan me-remount-nya dan melempar
  // pembeli yang sedang menggeser kembali ke foto pertama; menambah slide di
  // ujung tidak menggeser apa pun. `Promise.resolve` membungkus thenable
  // Flight yang `then`-nya tak mengembalikan rantai baru.
  const [feedData, setFeedData] = useState<ProductFeed>(EMPTY_PRODUCT_FEED);
  useEffect(() => {
    if (!feed) return;
    let alive = true;
    Promise.resolve(feed).then(
      (value) => {
        if (alive) setFeedData(value ?? EMPTY_PRODUCT_FEED);
      },
      () => {
        if (alive) setFeedData(EMPTY_PRODUCT_FEED);
      }
    );
    return () => {
      alive = false;
    };
  }, [feed]);
  // Langkah yang masih di dalam daftar asal pembeli (`?dari=`) membawa
  // konteksnya ke halaman berikutnya; langkah yang keluar melepasnya.
  const catalogKode = catalogMode?.kode ?? null;
  const nextHref = feedData.next
    ? productFeedHref(feedData.next.product.slug, {
        catalogKode,
        context: feedData.next.meta.context,
      })
    : null;
  const prevHref = feedData.prev
    ? productFeedHref(feedData.prev.product.slug, {
        catalogKode,
        context: feedData.prev.meta.context,
      })
    : null;

  // Satu pembuka untuk geseran, ketukan pil, dan tap pratinjau: ref-guard
  // menahan dorongan ganda (snap yang sempat bergetar memanggil dua kali).
  // `router.push` programatik baru menyentuh `history.pushState` saat commit,
  // jadi TopLoader dinyalakan eksplisit supaya pembeli langsung tahu geserannya
  // diterima.
  const [feedOpening, setFeedOpening] = useState<GalleryEdge | null>(null);
  const feedOpeningRef = useRef(false);
  const feedSafetyRef = useRef<number | null>(null);
  const openFeed = useCallback(
    (edge: GalleryEdge) => {
      const href = edge === "trailing" ? nextHref : prevHref;
      if (!href || feedOpeningRef.current) return;
      feedOpeningRef.current = true;
      setFeedOpening(edge);
      markFeedHintSeen();
      topLoader.startNavigation();
      router.push(href);
      feedSafetyRef.current = window.setTimeout(() => {
        feedSafetyRef.current = null;
        feedOpeningRef.current = false;
        setFeedOpening(null);
      }, FEED_OPEN_SAFETY_MS);
    },
    [nextHref, prevHref, router]
  );
  useEffect(() => {
    return () => {
      if (feedSafetyRef.current !== null) window.clearTimeout(feedSafetyRef.current);
    };
  }, []);

  // Warm-up: prefetch PENUH tujuan saat niat pembeli jelas (menyeret masuk
  // pratinjau / diam di foto ujung) — sekali per tujuan. Lihat `prefetchFull`.
  const prefetchedRef = useRef<Set<string>>(new Set());
  const warmFeed = useCallback(
    (edge: GalleryEdge) => {
      const href = edge === "trailing" ? nextHref : prevHref;
      if (!href || prefetchedRef.current.has(href)) return;
      prefetchedRef.current.add(href);
      prefetchFull(router, href);
    },
    [nextHref, prevHref, router]
  );

  // Cover pratinjau baru dimuat saat pembeli mendekati ujungnya (lengket:
  // sekali dimuat tak dilepas lagi).
  const lastPhotoIndex = Math.max(gallery.images.length - 1, 0);
  const [peekArmed, setPeekArmed] = useState({ next: false, prev: false });
  useEffect(() => {
    setPeekArmed((current) => {
      const next = current.next || activeImage >= lastPhotoIndex - 1;
      const prev = current.prev || activeImage <= 1;
      return next === current.next && prev === current.prev ? current : { next, prev };
    });
  }, [activeImage, lastPhotoIndex]);

  // Petunjuk sekali-pakai per perangkat, hanya bila memang ada produk berikutnya.
  const [feedHint, setFeedHint] = useState(false);
  useEffect(() => {
    if (feedData.next) setFeedHint(!readFeedHintSeen());
  }, [feedData.next]);
  // Produk ready stock: default ke varian yang masih ada stok (bukan yang habis).
  const stockTracked = isStockTracked(product);
  // `ProductVariation | undefined` EKSPLISIT: di mode malas `variations` kosong
  // sehingga `variations[0]` undefined saat render pertama (dan produk lama
  // 0-varian pun sudah bisa undefined sebelum perubahan ini — tipe lamanya
  // menjanjikan sesuatu yang tak dijamin runtime).
  const [selectedVariation, setSelectedVariation] = useState<
    ProductVariation | undefined
  >(
    () =>
      // Varian yang dituju `?v=` — SENGAJA tanpa syarat stok: tautan varian
      // habis (feed/JSON-LD "out of stock") harus mendarat di varian itu dan
      // mengatakan habis, bukan diam-diam menukarnya dengan varian lain.
      // Mode malas: `variations` kosong → selalu undefined di sini; pilihannya
      // lewat `selectedOptionIds` di bawah.
      (initialSelection?.optionIds
        ? findVariationByOptionIds(product.variations, initialSelection.optionIds)
        : undefined) ??
      (stockTracked
        ? firstInStockVariation(product.variations) ?? product.variations[0]
        : product.variations[0])
  );
  // ─── MODE MALAS (lazyVariants.ts) ──────────────────────────────────────────
  // Produk preorder dengan ribuan kombinasi tidak mengirim daftar variannya.
  // Pemilihan jadi berbasis option id, dan SATU kombinasi diresolusi saat
  // pilihannya lengkap. Semua kombinasi tetap bisa dipesan.
  // ─── KUOTA PO (mig 3970) ───────────────────────────────────────────────────
  // Hanya produk ber-toggle "Stok PO Terbatas" yang menembak RPC-nya; PO biasa
  // (mayoritas) tidak membayar round-trip apa pun.
  const poLimited = product.poLimited ?? false;
  const [quotaUsed, setQuotaUsed] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!poLimited) {
      setQuotaUsed({});
      return;
    }
    let alive = true;
    fetchPreorderQuotaUsed(product.id).then((map) => {
      if (alive) setQuotaUsed(map);
    });
    return () => {
      alive = false;
    };
  }, [poLimited, product.id]);

  const variantsLazy = product.variantsLazy ?? false;
  // ─── KOMBINASI YANG DIMATIKAN ADMIN (mig 5830) ─────────────────────────────
  // `is_active = false` pada barisnya. Pembeli TIDAK BISA melihat baris itu
  // (RLS menuntut `is_active`), jadi daftarnya dikirim terpisah oleh
  // `getPublicProductBySlug`; tanpa itu tombol variasinya tetap bisa diketuk
  // dan penolakannya baru muncul di checkout (bug live natasyashop).
  // Produk TERTUTUP (B1): yang dikirim daftar yang DIJUAL; setiap kombinasi
  // di luarnya dikunci lewat himpunan yang sama (`pickerBlockedSet`).
  const blockedComboSet = useMemo(
    () =>
      pickerBlockedSet({
        groups: product.variantGroups,
        blockedCombos: product.blockedOptionCombos,
        sellableCombos: product.sellableOptionCombos,
      }),
    [product.variantGroups, product.blockedOptionCombos, product.sellableOptionCombos]
  );
  // Catatan "grup lain ikut bergeser" — diisi `VariantGroupSelector` di setiap
  // klik mode non-malas (daftar kosong = tak ada yang bergeser, catatan hilang).
  const [variantShift, setVariantShift] = useState<VariantShift[]>([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(() =>
    // `?v=` dulu (tautan feed/JSON-LD/jalan pulang login — diurai server).
    // Kombinasi bawaan (opsi pertama tiap grup) bisa termasuk yang dimatikan
    // admin — halaman lalu terbuka dengan tombol pesan yang sudah mati dan
    // pembeli harus menebak kombinasi mana yang dijual.
    variantsLazy
      ? initialSelection?.optionIds ??
        firstSelectableSelection(product.variantGroups, blockedComboSet)
      : []
  );
  const [resolvingVariant, setResolvingVariant] = useState(false);
  // Cache per-kombinasi: memilih bolak-balik antar opsi tak menembak DB ulang.
  const resolvedCache = useRef(new Map<string, ProductVariation | null>());
  // Kunci kombinasi yang SEKARANG terlihat di layar — dibaca `addToCart`
  // SESUDAH menunggu RPC materialisasi. Tombol opsi tetap hidup selama RPC
  // berjalan, jadi pembeli bisa berganti pilihan di tengah jalan; tanpa
  // pembanding ini closure lama menambahkan kombinasi yang sudah ia tinggalkan.
  const selectionKeyRef = useRef(optionSelectionKey(selectedOptionIds));
  useEffect(() => {
    selectionKeyRef.current = optionSelectionKey(selectedOptionIds);
  }, [selectedOptionIds]);
  /**
   * Hasil resolusi untuk kombinasi yang SEDANG dipilih. `variation: null` =
   * kombinasi SAH tapi barisnya belum bermateri (normal setelah A) — beda dari
   * "masih memuat", dan bedanya harus terlihat di UI: yang pertama boleh
   * dipesan (barisnya dibuat saat menambah ke keranjang), yang kedua tidak.
   *
   * Disimpan sebagai STATE, bukan dibaca dari `resolvedCache` saat render:
   * ref tak memicu render ulang, sehingga kombinasi tak-bermateri (yang
   * membiarkan `selectedVariation` tetap undefined) tak akan pernah
   * memperbarui tombolnya.
   */
  const [lazyResolved, setLazyResolved] = useState<{
    key: string;
    variation: ProductVariation | null;
  } | null>(null);

  useEffect(() => {
    if (!variantsLazy) return;
    if (!isSelectionComplete(product.variantGroups, selectedOptionIds)) return;

    const key = optionSelectionKey(selectedOptionIds);
    const cached = resolvedCache.current.get(key);
    let alive = true;

    // FASE 4 — warisan harga untuk kombinasi yang barisnya BELUM ADA.
    // Dipanggil dari KEDUA cabang (cache hit maupun fetch): cache menyimpan
    // BARIS, bukan turunan harganya, jadi cabang cache yang berhenti di
    // `setInheritedPricing(null)` akan membuat kombinasi yang tadi berharga
    // berubah jadi "harga menyusul" begitu pembeli memilihnya lagi.
    const terapkanWarisan = async (
      variant: Product["variations"][number] | null | undefined
    ) => {
      // Baris yang SUDAH BERHARGA adalah kebenaran tunggalnya. Baris yang ada
      // tapi tanpa harga ikut bertanya ke RPC sejak 2026-10-20 — resolver
      // checkout memanggil RPC yang sama (lihat `shouldUseInheritedPricing`).
      if (!shouldUseInheritedPricing(variant)) {
        if (alive) setInheritedPricing(null);
        return;
      }
      const cachedWarisan = inheritedCache.current.get(key);
      if (cachedWarisan !== undefined) {
        if (alive) setInheritedPricing(cachedWarisan);
        return;
      }
      const inherited = await resolvePublicInheritedPricing(
        product.id,
        selectedOptionIds,
        getClientStoreId()
      );
      inheritedCache.current.set(key, inherited);
      // Pilihan bisa berganti selagi RPC berjalan; tanpa penjaga ini harga
      // kombinasi LAMA bisa mendarat di kombinasi yang sedang terlihat.
      if (alive) setInheritedPricing(inherited);
    };

    if (cached !== undefined) {
      setSelectedVariation(cached ?? undefined);
      setLazyResolved({ key, variation: cached });
      setResolvingVariant(false);
      setInheritedPricing(null);
      void terapkanWarisan(cached);
      return () => {
        alive = false;
      };
    }

    setResolvingVariant(true);
    setInheritedPricing(null);
    resolvePublicVariant(product.id, selectedOptionIds, getClientStoreId())
      .then(async (variant) => {
        if (!alive) return;
        resolvedCache.current.set(key, variant);
        setSelectedVariation(variant ?? undefined);
        setLazyResolved({ key, variation: variant });
        await terapkanWarisan(variant);
      })
      .catch(() => {
        if (!alive) return;
        // Gagal MENGHUBUNGI DB ≠ "kombinasi belum bermateri". Jangan menandainya
        // resolved: menandainya akan membuka tombol pesan atas kombinasi yang
        // harganya belum tentu kita ketahui.
        setSelectedVariation(undefined);
        setLazyResolved(null);
      })
      .finally(() => {
        if (alive) setResolvingVariant(false);
      });

    return () => {
      // Balapan: pilihan berganti sebelum resolusi selesai. Tanpa penjaga ini
      // jawaban LAMA bisa mendarat belakangan dan menimpa varian yang benar —
      // pembeli lalu memesan kombinasi yang tidak ia pilih.
      alive = false;
    };
  }, [variantsLazy, product.id, product.variantGroups, selectedOptionIds]);
  // FASE 4 (mig 5510): harga yang AKAN diwarisi kombinasi terpilih. Diisi hanya
  // di mode malas, dan hanya saat barisnya belum ada / belum berharga —
  // jawabannya datang dari RPC yang SAMA dengan yang menulis harga baris itu,
  // jadi angka di layar mustahil beda dari angka yang dikunci nota.
  // Cache warisan harga per kombinasi — cermin `resolvedCache` untuk barisnya.
  // Tanpa ini setiap kali pembeli bolak-balik antar opsi akan menembak RPC lagi.
  const inheritedCache = useRef<
    Map<string, Awaited<ReturnType<typeof resolvePublicInheritedPricing>>>
  >(new Map());
  const [inheritedPricing, setInheritedPricing] = useState<
    Awaited<ReturnType<typeof resolvePublicInheritedPricing>>
  >(null);
  // SUMBU HARGA (2026-10-20, mig 7680) — mode BIASA (daftar varian lengkap):
  // baris yang ADA tapi tanpa harga pada produk ber-sumbu hanya boleh berharga
  // dari warisan slot — RPC yang SAMA dengan resolver checkout, jadi angka di
  // layar = angka yang dikunci. Mode malas sudah memanggilnya lewat
  // `terapkanWarisan`. Produk tanpa sumbu (`priceAxis === false`) tak perlu
  // bertanya: aturan lama berlaku dan RPC-nya pasti NULL (sumbu = semua grup).
  useEffect(() => {
    if (variantsLazy) return;
    const variant = selectedVariation;
    const optionIds = variant?.optionIds ?? [];
    if (
      !variant ||
      !shouldUseInheritedPricing(variant) ||
      product.priceAxis === false ||
      optionIds.length === 0
    ) {
      setInheritedPricing(null);
      return;
    }
    let alive = true;
    const key = `v:${variant.id}`;
    const cached = inheritedCache.current.get(key);
    if (cached !== undefined) {
      setInheritedPricing(cached);
      return;
    }
    void resolvePublicInheritedPricing(product.id, optionIds, getClientStoreId()).then(
      (inherited) => {
        inheritedCache.current.set(key, inherited);
        if (alive) setInheritedPricing(inherited);
      }
    );
    return () => {
      alive = false;
    };
  }, [variantsLazy, selectedVariation, product.id, product.priceAxis]);
  const [qty, setQty] = useState(initialSelection?.qty ?? 1);
  // Kombinasi malas gagal disiapkan saat menambah ke keranjang: dialognya
  // sendiri, bukan dialog keranjang — bug live 2026-09-02, kegagalan RPC
  // dipetakan ke dialog "Ada keranjang produk lain" pada keranjang kosong,
  // dan pembeli tak pernah diberi tahu bahwa ia perlu masuk.
  const [variantBlock, setVariantBlock] = useState<{
    reason: PreorderVariantFailure;
    /** Pesan RPC untuk REJECTED — ditulis untuk pembeli, ditampilkan apa adanya. */
    message?: string;
  } | null>(null);
  const [showAdded, setShowAdded] = useState(false);
  // Umpan balik tombol "+ Keranjang" mode katalog. TERPISAH dari `showAdded`
  // karena ia juga harus bisa mengatakan "keranjang penuh" — satu boolean tak
  // bisa membedakan berhasil dari ditolak.
  const [catalogCartMsg, setCatalogCartMsg] = useState<string | null>(null);

  const [sharingPhoto, setSharingPhoto] = useState(false);
  const [photoShareMsg, setPhotoShareMsg] = useState<string | null>(null);
  // Jam halaman ini MENERIMA datanya di klien. Dipakai sebagai cap waktu
  // CADANGAN pesan bagikan saat pembacaan ulang stok gagal: pesannya tetap
  // terkirim, tapi jamnya menunjuk kapan angka itu benar-benar dibaca — bukan
  // kapan tombol ditekan. Lihat catatan panjang di `src/lib/shareProduct.ts`.
  const [clientLoadedAt] = useState(() => new Date());
  // Potret stok untuk pesan bagikan, dibaca DI LATAR. Lihat catatan panjang di
  // `shareToWhatsApp` — jalur kliknya WAJIB sinkron, jadi angkanya harus sudah
  // siap sebelum tombol ditekan.
  const [stockRead, setStockRead] = useState<{
    snapshot: ProductStockSnapshot;
    readAt: Date;
  } | null>(null);
  // Deskripsi tampil terpotong (line-clamp) → "Tampilkan selengkapnya" membuka penuh.
  const descRef = useRef<HTMLParagraphElement>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descOverflows, setDescOverflows] = useState(false);

  // Promo kampanye (mig 3470) — berlaku juga di mode katalog reseller, karena
  // promo adalah harga PUBLIK (beda dari diskon level yang memang disembunyikan
  // di katalog reseller).
  const rawPromos = usePromosFor(product);
  // SATU SUMBU LEVEL untuk seluruh komponen ini. `usePromosFor` menyaring
  // memakai level SESI pengunjung, sedangkan di mode katalog `group` sengaja
  // dipaksa null (baris atas) — dua sumbu berbeda dalam satu komponen. Tanpa
  // penyelarasan ini, pelanggan ber-level yang membuka katalog reseller SIAPA
  // PUN melihat pil "🔥 <nama kampanye>" milik kampanye ber-level, padahal
  // harga yang tercetak (dan yang dikirim ke pesan WhatsApp) adalah harga
  // publik — nama kampanye internal terpasang sebagai klaim promo di halaman
  // white-label yang harganya tak pernah menghormatinya. Idempoten di toko
  // utama, karena di sana `group === rawGroup`.
  const promos = useMemo(
    () => promosForLevel(rawPromos, group?.slot ?? null),
    [rawPromos, group]
  );
  // Promo TAMU untuk harga pixel iklan (B7, PR-IG-4) — dari daftar kampanye
  // utuh, bukan dari `rawPromos` yang sudah tersaring level sesi.
  const guestPromos = useGuestPromosFor(product);
  // SATU panggilan untuk angka, angka coret, DAN kampanye pemenang. Sebelumnya
  // ketiganya dihitung terpisah di sini, dan "harga normal" dirumuskan sendiri
  // sebagai `selectedVariation?.price_normal ?? product.price_normal` — memakai
  // `products.price_normal` (MIN seluruh varian) yang sudah lama dilarang jadi
  // angka coret. Pada varian tanpa harga sendiri hasilnya: harga perwakilan
  // Rp 521.250 disandingkan dengan "normal" Rp 100.000, `getDiscountInfo`
  // menyimpulkan tak ada diskon, dan potongan 25% yang nyata lenyap dari layar
  // (natasyashop #2095). `resolveSelectionPrice` memulangkan pasangan yang
  // memang sejalan — lihat catatan `SelectionPrice.normalPrice`.
  //
  // Label ukuran (mig 3670) ikut di dalam resolver, jadi badge dan harga
  // mustahil menunjuk kampanye BERBEDA pada produk ber-aturan-ukuran eksklusif.
  // Label varian untuk pesan WhatsApp (tamu & katalog) DAN untuk aturan promo
  // ber-ukuran pada harga warisan Fase 4 — karena itu ia dihitung SEBELUM
  // `resolveSelectionPrice`, bukan sesudahnya. Di mode malas
  // kombinasi yang belum bermateri tak punya `selectedVariation`, padahal
  // pembeli sudah memilihnya — tanpa turunan ini pesan WA tiba di admin TANPA
  // baris "Varian" (ZURIAH: tanpa jenis/warna/ukuran).
  const selectedVariantLabel =
    selectedVariation?.name ??
    (variantsLazy && isSelectionComplete(product.variantGroups, selectedOptionIds)
      ? deriveVariantLabel(product.variantGroups, selectedOptionIds)
      : undefined);

  const priceCatalog = {
    variations: product.variations,
    priceNormal: product.price_normal,
    // Mode malas mengosongkan `variations`, jadi ringkasan inilah satu-satunya
    // cara halaman ini tahu harga produknya seragam atau campuran. Tanpa itu
    // varian tanpa harga dipajang seharga `products.price_normal` (MIN
    // seluruh varian) sementara checkout mengunci harga varian perwakilan —
    // di PROD layarnya Rp 75.000, notanya Rp 521.250 (natasyashop #2095).
    priceRange: product.variantPriceRange,
    priceUncertain: product.variantPriceUncertain,
    // Sumbu harga (mig 7680): tak termuat = tak diketahui → fail-closed.
    priceAxis: product.priceAxis ?? null,
    inherited: inheritedPricing,
    inheritedName: selectedVariantLabel
  };
  // Kombinasi mode malas yang barisnya belum ada dinilai sebagai varian TANPA
  // harga sendiri (bughunt D16-13) — bukan "tak ada pilihan", yang dulu
  // memajang harga MIN produk lalu memasukkannya ke keranjang. Satu nilai
  // untuk harga pembeli DAN harga pixel tamu di bawah.
  const pricedVariation = pricingSelection({
    variation: selectedVariation,
    variantsLazy,
    groups: product.variantGroups,
    selectedOptionIds
  });
  const selection = resolveSelectionPrice(pricedVariation, priceCatalog, group, promos);
  const activePromo = selection.promo;
  // ANGKA YANG BOLEH DIPAKAI. Pada `unpricedVariant` `selection.price` milik
  // varian PERWAKILAN — memajangnya lalu memasukkannya ke keranjang persis bug
  // natasyashop #2095. `sellableSelectionPrice` memulangkan `undefined` di sana,
  // jadi layar & keranjang sama-sama jatuh ke "harga menyusul".
  const sellable = sellableSelectionPrice(selection);
  const selectedPrice = sellable.price;
  // Harga tambahan (mig 7900) yang TERMASUK di `selectedPrice` — sub-teks R1
  // "Termasuk tambahan ukuran XXL Rp 20.000" (tanpa "tidak kena diskon").
  const selectedExtra = sellable.priceExtra;
  const extraNote = priceExtraNote(
    selectedExtra,
    priceExtraSubject(
      product.variantGroups,
      selectedVariation?.optionIds ?? selectedOptionIds,
      product.variations
    )
  );
  // Harga pilihan yang SAMA bagi TAMU — satu-satunya harga yang boleh keluar ke
  // vendor iklan (B7, PR-IG-4). Bagi reseller `selectedPrice` adalah harga
  // levelnya, rahasia toko; bagi tamu keduanya identik.
  const pixelUnitPrice = guestSellablePrice(pricedVariation, priceCatalog, guestPromos);
  const normalPrice = selection.normalPrice;
  // Setelan harga produk belum lengkap untuk varian INI (produk punya varian
  // berharga dengan nilai berbeda-beda, varian terpilih tidak punya harga
  // sendiri). Harga yang bisa ditampilkan cuma milik varian LAIN, jadi ANGKANYA
  // ditahan — tapi PESANANNYA tetap jalan sebagai "harga menyusul" (keputusan
  // pemilik 2026-09-07, mengoreksi blokir 2026-09-05). Flag ini karena itu cuma
  // menyalakan catatan di layar, tidak lagi mematikan tombol.
  const priceNotSet = selection.unpricedVariant;
  // Saat promo FINAL berlaku, angka di layar BUKAN harga grup — diskon level
  // memang diabaikan ("promo menang mutlak"). Menampilkan badge "Harga <Grup>"
  // di atasnya akan berbohong, dan justru paling menyesatkan pada kasus yang
  // sudah kita akui pahit: reseller yang harga levelnya lebih murah dari promo.
  // Pada promo HARGA CORET diskon level tetap membentuk harga (`tier2`), jadi
  // labelnya benar dan ditampilkan.
  const badgeLabel = activePromo && !selection.tier2 ? null : getPriceBadgeLabel(group);
  // Pil potongan & angka coret dari sumber yang SAMA dengan harga: "10% + 30%"
  // (promo coret + level; 🔥 ada di chip kampanye di bawahnya), "-10%", "-30%".
  // Angka coret = harga sebelum potongan TERAKHIR (reseller di promo coret →
  // harga coret 90.000; tamu / satu potongan → harga umum).
  const discountBadge =
    selectedPrice === undefined
      ? null
      : discountBadgeLabel({ ...selection, price: selectedPrice }, { flame: false });
  const strikePrice = discountBadge ? selection.strikePrice : undefined;
  // Rantai harga untuk pembeli ber-level di promo coret — supaya "10% + 30%"
  // bisa dibaca sebagai angka: umum → harga coret → harga level.
  const priceChain =
    selection.tier2 && normalPrice !== undefined && strikePrice !== undefined && selectedPrice !== undefined
      ? {
          umum: normalPrice,
          promo: selection.cuts[0],
          // Potongan dihitung atas DASAR; tambahan ditempel di ujung rantai.
          coret: strikePrice - selectedExtra,
          level: selection.cuts[1],
          extra: selectedExtra,
          akhir: selectedPrice
        }
      : null;
  // Sisa waktu kampanye (R9) — helper yang sama dengan chip di kartu.
  const promoNow = usePromoNow();
  const promoTimeLeft = activePromo && promoNow ? promoTimeLeftLabel(activePromo.endsAt, promoNow) : null;
  // Sisa kuota kampanye (mig 6020). Kampanye yang HABIS tak pernah sampai ke
  // `activePromo` — `usePromosFor` sudah menggugurkannya — jadi chip ini hanya
  // muncul selama promonya benar-benar masih bisa dipakai.
  const promoRemaining = usePromoRemaining();
  const promoLeftLabel = activePromo
    ? promoRemainingLabel(activePromo.campaignId, promoRemaining)
    : null;
  // DP = konsep checkout toko — tak relevan di katalog reseller.
  const dpBadgeLabel = catalogMode ? null : getDpBadgeLabel(product);
  // Tombol "Materi iklan" → channel Telegram MEREK (diisi admin di
  // /admin/brands/[id]). Merek tanpa link = tombolnya tidak dirender sama
  // sekali. Dinormalisasi lagi di klien supaya nilai lama/ngawur di DB tak
  // pernah jadi href rusak — `null` di sini artinya sembunyikan.
  //
  // SENGAJA MATI DI KATALOG RESELLER: `/toko/[kode]` white-label (logo toko
  // induk pun disembunyikan). Menautkan channel merek ke pembeli akhir reseller
  // membuka jalan mereka membeli langsung ke sumbernya — persis yang dijaga
  // mode katalog.
  const brandTelegramHref = catalogMode
    ? null
    : normalizeBrandTelegramUrl(product.brandTelegramUrl);
  // Logo merek di badge (mig 096). Dinormalisasi ULANG saat render dengan
  // alasan yang sama seperti link Telegram: nilai tersimpan bisa berasal dari
  // baris lama / edit manual di DB, dan teks sembarang akan tayang sebagai
  // gambar RUSAK. Tidak dikenali → jatuh ke ikon Tag.
  //
  // BEDA dari tombol Telegram: logo TETAP HIDUP di katalog reseller. Yang
  // dijaga mode katalog adalah kanal jualan sumbernya, bukan identitas merek
  // yang dijual — halaman `/toko/[kode]/merek` pun sudah menampilkan logo ini.
  const brandIconUrl = normalizeBrandIconUrl(product.brandIconUrl);
  // Badge gudang (mig 7260): mode katalog reseller TAK PERNAH menampilkannya,
  // walau pemanggil keliru mengisi prop-nya.
  const shipFromWarehouse = catalogMode ? "" : (warehouseName ?? "").trim();
  const canOrder = isOrderableStatus(product.status, product.productType);
  // Produk Pre-Order yang harganya belum ditetapkan: sembunyikan teks "Harga
  // belum tersedia" (beserta badge harga grup). Catatan "Harga menyusul" di bawah
  // tetap tampil sebagai info. Produk ready stock tak terpengaruh.
  const hidePrice =
    priceNotSet ||
    (product.productType !== "ready_stock" && selectedPrice === undefined);
  // HARGA PERKIRAAN (mig 6840) — PAJANGAN SAJA: tak pernah masuk keranjang,
  // nota, DP, maupun pixel (`selectedPrice` tetap undefined = harga menyusul).
  // Hanya di tempat catatan "Harga menyusul" tampil, dan hanya bila produk tak
  // punya satu pun harga asli (`effectivePriceEstimate`) — angka yang SAMA
  // dengan feed Katalog Instagram. Produk seperti itu tak punya setelan harga
  // yang "belum lengkap untuk varian ini": `priceNotSet` di sana (varian
  // bertambahan tanpa dasar, sumbu harga) tak menahan perkiraan.
  const productEstimate =
    selectedPrice === undefined && canOrder ? effectivePriceEstimate(product) : undefined;
  // Catatan "Harga menyusul" generik ditahan saat catatan khusus varian
  // (`priceNotSet`) tampil — dua kalimat yang mengatakan hal sama di satu layar.
  const showPriceFollowsNote =
    selectedPrice === undefined && canOrder && (!priceNotSet || productEstimate !== undefined);
  // Perkiraan VARIAN terpilih = perkiraan + tambahannya (bughunt D15-29).
  const priceEstimate =
    productEstimate === undefined ? undefined : variantPriceEstimate(productEstimate, selectedVariation);
  const estimateExtraNote =
    productEstimate === undefined || priceEstimate === undefined
      ? null
      : priceExtraNote(
          priceEstimate - productEstimate,
          priceExtraSubject(
            product.variantGroups,
            selectedVariation?.optionIds ?? selectedOptionIds,
            product.variations
          )
        );
  // Produk "langsung harga saja" punya 1 varian default (tanpa grup) → tak perlu
  // menampilkan selektor varian. Hanya tampilkan bila ada grup ATAU >1 varian.
  const hasVariationChoice =
    product.variantGroups.length > 0 || product.variations.length > 1;
  // Varian terpilih benar-benar SESUAI pilihan yang terlihat di layar? Di mode
  // malas `selectedVariation` bisa tertinggal satu langkah (hasil resolusi
  // sebelumnya) selagi kombinasi baru masih diambil. Menambahkan ke keranjang
  // pada jendela itu = memesan kombinasi yang TIDAK dipilih pembeli, jadi
  // tombolnya ditahan sampai varian yang ter-resolusi cocok dengan pilihannya.
  const lazySelectionSettled =
    !variantsLazy ||
    (!resolvingVariant &&
      // Hasil resolusi harus milik kombinasi yang SEKARANG terlihat di layar —
      // bukan sisa pilihan sebelumnya.
      lazyResolved?.key === optionSelectionKey(selectedOptionIds) &&
      // `variation: null` SAH: kombinasi belum bermateri, barisnya dibuat saat
      // menambah ke keranjang. Yang tak boleh lolos adalah "belum ter-resolusi".
      (lazyResolved.variation === null ||
        selectedOptionIds.every((id) =>
          selectedVariation?.optionIds?.includes(id)
        )));

  // Stok varian terpilih (hanya relevan untuk produk ready stock).
  const selectedStockQty = selectedVariation?.stockQty;
  const selectedInStock = !stockTracked || isVariationInStock(selectedVariation);
  // Sisa jatah PO varian terpilih. `null` = tak terbatas (toggle mati / varian
  // tanpa angka) — itu keadaan normal seluruh produk PO yang ada hari ini.
  const selectedQuotaRemaining = quotaRemaining(
    poLimited,
    selectedVariation?.poQuota,
    selectedVariation ? quotaUsed[selectedVariation.id] : undefined
  );
  const selectedQuotaExhausted = selectedQuotaRemaining === 0;
  // Baris kuota siap-render: sisa SELALU disebut selama produknya berkuota,
  // cermin baris "Stok tersedia: N pcs" milik ready stock. Nada & kalimatnya
  // dihitung di `quotaStorefrontLine` supaya bisa dites tanpa merender halaman.
  const quotaLine = quotaStorefrontLine(
    poLimited,
    selectedVariation?.poQuota,
    selectedVariation ? quotaUsed[selectedVariation.id] : undefined
  );
  const selectedAvailable = selectedInStock && !selectedQuotaExhausted;
  const maxQty = stockTracked
    ? selectedInStock
      ? selectedStockQty
      : undefined
    : (selectedQuotaRemaining ?? undefined);
  // ─── Kombinasi yang TIDAK BOLEH dipesan, dua sebab yang berbeda ────────────
  //
  // (1) DIMATIKAN ADMIN (`is_active = false`, mig 5830). Barisnya ada tapi tak
  //     terbaca pembeli (RLS), jadi `resolvePublicVariant` memulangkan `null`
  //     — sama persis dengan kombinasi yang belum bermateri. Daftar mati yang
  //     dikirim terpisah itulah yang membedakannya.
  //
  // (2) PO BERKUOTA + MODE MALAS: daftar kombinasi PO berkuota TERTUTUP
  //     (mig 3970 menolak materialisasi baru), jadi kombinasi tanpa baris
  //     PASTI ditolak RPC saat tombol ditekan. Sebelum ini tombolnya menyala
  //     dan pembeli baru tahu sesudah mengetuk. `variation === null` di sini
  //     BUKAN tebakan: ia jawaban resolusi yang sudah selesai untuk kombinasi
  //     yang sedang terlihat (`lazySelectionSettled` menjaga kuncinya cocok).
  const selectedComboBlocked =
    variantsLazy &&
    isSelectionComplete(product.variantGroups, selectedOptionIds) &&
    isComboBlocked(blockedComboSet, selectedOptionIds);
  // B1: produk TERTUTUP sama dengan PO berkuota — tanpa baris = tak dijual.
  const closedQuotaUnmaterialized =
    variantsLazy &&
    (poLimited || product.absentCombosSellable === false) &&
    lazySelectionSettled &&
    lazyResolved?.key === optionSelectionKey(selectedOptionIds) &&
    lazyResolved.variation === null;
  const variantUnavailable = selectedComboBlocked || closedQuotaUnmaterialized;

  // Wajib ada varian terpilih: produk lama 0-varian (pra-fix) tak bisa di-keranjang.
  // Di mode malas, kombinasi yang belum bermateri BOLEH dipesan walau
  // `selectedVariation` masih undefined — barisnya dibuat saat menambah ke
  // keranjang (`resolveOrCreatePreorderVariant`). Yang menjaga kebenaran di
  // sini `lazySelectionSettled`, bukan keberadaan baris.
  // Harga yang belum disetel SENGAJA tidak menggerbangi tombol ini: pesanan
  // tanpa harga adalah alur sah di sini (PO "harga menyusul"), dan yang menjaga
  // uang adalah `selectedPrice`/resolver checkout yang memulangkan `undefined`,
  // bukan penolakan pesanan (keputusan pemilik 2026-09-07).
  const canAdd =
    canOrder &&
    selectedAvailable &&
    !variantUnavailable &&
    (Boolean(selectedVariation) || variantsLazy) &&
    lazySelectionSettled;

  // Jaga qty tetap dalam batas stok saat berpindah varian (produk ready stock).
  useEffect(() => {
    if (maxQty !== undefined) {
      setQty((current) => Math.min(Math.max(1, current), maxQty));
    }
  }, [maxQty]);

  // Catat kunjungan untuk rail "Terakhir Dilihat" — juga di mode katalog:
  // beranda katalog kini cermin beranda toko dan punya rail yang sama
  // (localStorage per-perangkat, tak bocor lintas pengunjung). Baru dicatat
  // setelah `RECENT_VIEW_DWELL_MS`: produk yang cuma dilewati saat menggeser
  // feed tak boleh mendorong keluar produk yang sengaja dibuka.
  useEffect(() => {
    const timer = window.setTimeout(
      () => recordRecentView(product.slug),
      RECENT_VIEW_DWELL_MS
    );
    return () => window.clearTimeout(timer);
  }, [product.slug]);

  // Pixel iklan: ViewContent SEKALI per produk yang dibuka (ref menahan
  // penembakan ulang saat promo menyusul termuat). Harga yang dikirim =
  // harga PUBLIK — grup null DAN promo tamu (`guestPromos`, B7 PR-IG-4; dulu
  // promo level sesi, yang bagi reseller kehilangan kampanye umum): harga
  // khusus reseller tak bocor ke payload vendor iklan. Mode katalog reseller
  // (/toko/…) sudah tersaring guard path di dalam firePixelEvent.
  const viewTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (viewTrackedRef.current === product.id) return;
    viewTrackedRef.current = product.id;
    firePixelEvent(
      "ViewContent",
      buildViewContentPayload({
        productId: product.id,
        productName: product.name,
        price: getProductPrice(product, null, guestPromos),
      })
    );
  }, [product, guestPromos]);

  // Potensi poin dari Program Poin aktif yang cocok dengan produk ini.
  // Di mode katalog disembunyikan (program poin = fasilitas reseller toko).
  const [pointHints, setPointHints] = useState<string[]>([]);
  useEffect(() => {
    if (catalogMode) return;
    let alive = true;
    fetchActivePrograms()
      .then((programs) => {
        if (!alive) return;
        const matched = programsForProduct(
          {
            id: product.id,
            brand: product.brand,
            category: product.category,
            productType: product.productType,
          },
          programs,
        );
        setPointHints(Array.from(new Set(matched.map(productPointsHint))));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [product.id, product.brand, product.category, product.productType, catalogMode]);

  // Deteksi apakah deskripsi terpotong (perlu tombol "Tampilkan selengkapnya").
  // Diukur dalam keadaan ter-clamp (line-clamp-3) saat mount.
  useEffect(() => {
    const el = descRef.current;
    if (el) {
      setDescOverflows(el.scrollHeight > el.clientHeight + 1);
    }
  }, [product.description]);

  const scrollToCart = () => {
    window.setTimeout(() => {
      cartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const addToCart = async () => {
    if (!canAdd) return;

    // MATERIALISASI SAAT DIPESAN (A): kombinasi yang belum punya baris varian
    // dibuat SEKARANG — bukan saat pembeli sekadar memilih opsi, karena itu akan
    // memateralisasi kartesius lewat pintu belakang.
    let variation = selectedVariation;
    if (!variation && variantsLazy) {
      // RPC-nya MENULIS baris dan EXECUTE-nya hanya untuk sesi login — tapi
      // SENGAJA tidak digerbangi `isAuthenticated` di sini: nilai itu tetap
      // `false` juga saat resolusi sesi GAGAL (getUser timeout, jaringan),
      // padahal cookie sesinya sah. Yang memutuskan adalah jawaban RPC:
      // 42501/401 → LOGIN_REQUIRED (`classifyPreorderVariantFailure`), dan
      // tamu sungguhan memang berakhir di dialog "Masuk" lewat jalur itu.
      const requestedKey = optionSelectionKey(selectedOptionIds);
      setResolvingVariant(true);
      const resolved = await resolveOrCreatePreorderVariant(
        product.id,
        selectedOptionIds
      );
      setResolvingVariant(false);
      // Pilihan berganti selagi menunggu → jawaban ini milik kombinasi yang
      // sudah ditinggalkan. Jangan menambahkannya ke keranjang, jangan pula
      // menjadikannya `selectedVariation` (tombol akan menyala untuk pilihan
      // baru dengan varian lama di baliknya). Barisnya sendiri sah tercipta,
      // jadi cache-nya tetap diisi supaya pilihan itu tak menembak RPC lagi.
      const selectionMoved = selectionKeyRef.current !== requestedKey;
      if (!resolved.ok) {
        // Gagal menyiapkan baris → JANGAN menebak id, dan JANGAN memakai
        // dialog keranjang: alasannya sesi (minta masuk) atau server (coba
        // lagi), bukan produk lain di keranjang.
        if (!selectionMoved) {
          setVariantBlock({ reason: resolved.reason, message: resolved.message });
        }
        return;
      }
      variation = {
        id: resolved.id,
        name: deriveVariantLabel(product.variantGroups, selectedOptionIds),
        sku: "",
        optionIds: selectedOptionIds,
      };
      resolvedCache.current.set(requestedKey, variation);
      if (selectionMoved) return;
      setSelectedVariation(variation);
    }
    if (!variation) return;

    const safeQty = maxQty !== undefined ? Math.min(qty, maxQty) : qty;
    // Tambahan ikut sebagai pasangan harganya (bughunt D14-31).
    addItem(product, variation, safeQty, selectedPrice, selectedPrice !== undefined ? selectedExtra : undefined);

    // Pixel iklan: AddToCart hanya pada cabang SUKSES (keranjang benar-benar
    // bertambah). Harga = harga TAMU pilihan ini (`pixelUnitPrice`), BUKAN
    // `selectedPrice`: bagi reseller itu harga levelnya — rahasia toko (B7,
    // PR-IG-4). undefined ("harga menyusul") → payload tanpa value, bukan 0.
    firePixelEvent(
      "AddToCart",
      buildAddToCartPayload({
        productId: product.id,
        productName: product.name,
        qty: safeQty,
        unitPrice: pixelUnitPrice,
      })
    );

    setShowAdded(true);
    window.setTimeout(() => setShowAdded(false), 1600);
    scrollToCart();
  };

  // Produk ini punya angka yang layak dicetak di pesan bagikan? Aturannya di
  // `productShareMessage.ts` — SATU sumber dengan tombol Bagikan di Jelajah.
  const shareNeedsStock = productShareNeedsStock(product);

  // Potret stok DI LATAR. Jalur klik tombol Bagikan wajib sinkron (lihat
  // catatan di `shareToWhatsApp`), jadi pembacaannya dipindah ke sini.
  //
  // Dibaca ulang saat halaman KEMBALI TERLIHAT karena itulah alur reseller yang
  // sebenarnya: buka produk → pindah ke WhatsApp → kembali lalu bagikan. Tanpa
  // itu, halaman yang menganggur setengah hari akan mengirim angka setengah
  // hari yang lalu — jujur menurut cap waktunya, tapi tak berguna.
  //
  // Harganya satu query sempit (`id, stock, po_quota`) untuk satu produk, dan
  // hanya untuk produk NON-malas yang variannya ≤200 (LAZY_VARIANT_THRESHOLD)
  // — satu halaman PostgREST, bukan paginasi.
  useEffect(() => {
    if (!shareNeedsStock) {
      setStockRead(null);
      return;
    }
    let alive = true;
    const read = () => {
      fetchProductStockSnapshot(product.id, { withQuota: poLimited }).then(
        (snapshot) => {
          // Potret GAGAL (null) sengaja TIDAK menimpa potret lama: yang lama
          // masih benar menurut cap waktunya sendiri, sedangkan membuangnya
          // membuat pesan kehilangan blok stoknya karena gangguan sesaat.
          if (alive && snapshot) setStockRead({ snapshot, readAt: new Date() });
        }
      );
    };
    read();
    const onVisible = () => {
      if (document.visibilityState === "visible") read();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [shareNeedsStock, poLimited, product.id]);

  // Label harga & blok stok pesan bagikan: logikanya di `productShareMessage.ts`
  // (dipindah apa adanya), dipakai juga tombol Bagikan di Jelajah supaya pesan
  // dari kedua tempat identik. Blok stok tetap dirakit SINKRON dari potret yang
  // sudah dibaca di latar (`stockRead`); cadangan jamnya `clientLoadedAt`.
  const buildSharePriceLabel = (): string | null => sharePriceLabel(product, promos);

  const buildShareStockLines = (): string[] | undefined => {
    return shareStockLines(product, {
      stockRead,
      quotaUsed,
      fallbackReadAt: clientLoadedAt,
    });
  };

  const buildShareMessage = (url: string, storeLabel: string | null): string => {
    return buildProductShareText({
      productName: product.name,
      storeName: storeLabel,
      priceLabel: buildSharePriceLabel(),
      typeLabel: stockTracked ? "Ready Stock" : "Pre-Order",
      stockLines: buildShareStockLines(),
      url,
    }, waTemplates);
  };

  // Bagikan produk ke WhatsApp. Mode katalog: URL & branding memakai katalog
  // reseller, jadi pesan yang diteruskan pembeli akhir tetap mendarat di
  // katalog itu — bukan di toko induk.
  //
  // ⚠️ HANDLER INI WAJIB SINKRON, DAN `window.open` WAJIB LANGSUNG MEMBAWA URL
  // TUJUANNYA. Versi pertama fitur stok ini membaca ulang stok saat klik,
  // sehingga handlernya asinkron; untuk menyelamatkan gestur klik ia membuka
  // tab penampung lebih dulu (`window.open("", "_blank")`) lalu mengisi
  // alamatnya setelah pesannya jadi. Itu bug LIVE: di browser DALAM-APLIKASI
  // (WhatsApp, Instagram, Facebook) — persis tempat reseller membuka link
  // produk — `window.open("")` tidak menghasilkan about:blank melainkan
  // membuka SITUS INI lagi, jadi tombol Bagikan mendarat di website alih-alih
  // di WhatsApp.
  //
  // Karena itu angkanya disiapkan DI LATAR (`stockRead`) dan perakitan pesan di
  // sini murni sinkron. Cap waktunya tetap jujur: yang dicetak adalah jam
  // potret itu dibaca, bukan jam tombol ditekan.
  const shareToWhatsApp = () => {
    if (typeof window === "undefined") return;
    const url = catalogMode
      ? buildCatalogProductUrl(catalogShareOrigin(), catalogMode.kode, product.slug)
      : buildProductShareUrl(window.location.origin, product.slug);
    const text = buildShareMessage(
      url,
      catalogMode ? catalogMode.resellerName : storeName
    );
    window.open(buildWhatsAppShareLink(text), "_blank", "noopener,noreferrer");
  };

  // #1 Bagikan versi KATALOG SAYA (toko utama, reseller login): link produk
  // versi katalog reseller sendiri → pembeli akhir order ke dia.
  const shareMyCatalog = () => {
    if (!myCatalogSlug || typeof window === "undefined") return;
    const url = buildCatalogProductUrl(
      catalogShareOrigin(),
      myCatalogSlug,
      product.slug
    );
    const text = buildShareMessage(
      url,
      displayResellerName(customer?.storeName, customer?.fullName ?? "")
    );
    window.open(buildWhatsAppShareLink(text), "_blank", "noopener,noreferrer");
  };

  // Tambah ke keranjang KATALOG — lokal saja: tak ada RPC, tak ada baris varian
  // dibuat, tak ada stok bergerak. Itu sebabnya ia tak perlu
  // `resolveOrCreatePreorderVariant` seperti `addToCart` toko utama: yang
  // disimpan hanya LABEL varian, dan label selalu ada.
  //
  // Gerbangnya `canAdd` yang sama persis dengan tombol order — di dalamnya ada
  // `lazySelectionSettled`, sehingga kombinasi mode malas yang belum selesai
  // diresolusi tak bisa masuk keranjang membawa harga/berat kombinasi
  // SEBELUMNYA.
  const addToCatalogCart = () => {
    if (!catalogMode || !catalogCart || !canAdd) return;
    const ok = catalogCart.add({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      // Varian default tunggal ("Standar") bukan pilihan → tak perlu disebut.
      variantLabel: hasVariationChoice ? (selectedVariantLabel ?? "") : "",
      unitPrice: selectedPrice ?? null,
      // Tambahan yang termasuk di harga (mig 7900) — disebut di pesan WA.
      ...(selectedPrice !== undefined && selectedExtra > 0 ? { priceExtra: selectedExtra } : {}),
      weightGram: selectedVariation?.weightGram ?? null,
      qty: maxQty !== undefined ? Math.min(qty, maxQty) : qty,
      ...(product.images[0] ? { image: product.images[0] } : {}),
    });
    setCatalogCartMsg(
      ok
        ? "✓ Ditambahkan ke keranjang"
        : "Keranjang penuh — kirim pesanan ini dulu"
    );
    window.setTimeout(() => setCatalogCartMsg(null), 2000);
  };

  // Order via WhatsApp untuk pengunjung TAMU (belum login) di toko UTAMA.
  // Checkout menuntut login (CartCheckout → "Masuk untuk Membuat Pesanan"),
  // jadi tamu diberi jalur pesan tanpa akun: chat langsung ke nomor admin.
  // Bentuk pesannya SENGAJA memakai ulang helper mode katalog (satu format
  // pesan order untuk seluruh app); di sini penerimanya = toko itu sendiri.
  // Harga yang dikirim = harga NORMAL — tamu tak punya grup (group=null),
  // jadi diskon reseller tak pernah ikut terbawa.
  const orderViaWhatsAppAsGuest = () => {
    if (catalogMode || !canAdd || typeof window === "undefined") return;
    const url = buildProductShareUrl(window.location.origin, product.slug);
    const message = buildCatalogOrderMessage({
      resellerName: storeName,
      productName: product.name,
      // Varian default tunggal ("Standar") = bukan pilihan → tak perlu disebut.
      variantName: hasVariationChoice ? selectedVariantLabel : null,
      qty: maxQty !== undefined ? Math.min(qty, maxQty) : qty,
      priceLabel:
        selectedPrice !== undefined
          ? `${formatPrice(selectedPrice)}${
              selectedExtra > 0 ? ` (termasuk tambahan ${formatPrice(selectedExtra)})` : ""
            }`
          : null,
      url,
    }, waTemplates);
    window.open(
      buildCatalogOrderWaUrl(adminWhatsapp, message),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const sharePhoto = async () => {
    // Daftar GALERI, bukan `product.images`: sesudah foto opsi ikut masuk
    // slider, `activeImage` beralamat ke galeri gabungan — membacanya dari
    // daftar lama membagikan foto yang BUKAN yang sedang dilihat pembeli
    // (dan `undefined` untuk tiap slide foto opsi).
    const imageUrl = gallery.images[activeImage];
    if (!imageUrl || sharingPhoto) return;
    setSharingPhoto(true);
    try {
      const result = await sharePhotoToStatus(imageUrl, product.name);
      if (result === "shared") {
        setPhotoShareMsg("Pilih Status di WhatsApp untuk bagikan foto 📸");
      } else if (result === "downloaded") {
        setPhotoShareMsg("Foto diunduh — unggah manual ke Status WA 📥");
      }
      // 'cancelled' = user tutup share sheet sendiri, tidak perlu pesan.
    } catch {
      setPhotoShareMsg("Gagal memuat foto, coba lagi");
    } finally {
      setSharingPhoto(false);
      window.setTimeout(() => setPhotoShareMsg(null), 2800);
    }
  };

  // Tombol Order via WhatsApp hanya untuk TAMU (belum login) di toko utama —
  // gerbangnya pindah ke helper murni (`showGuestWaOrder`, diuji) sejak
  // saklarnya ada: lima lapis, dua di antaranya fail-closed, alasan tiap lapis
  // ada di sana. Pola yang sama dengan `showPartnerProductCta` di bawah.
  const showGuestWaCta = showGuestWaOrder({
    catalogMode: Boolean(catalogMode),
    settingEnabled: guestWaOrderEnabled,
    customerLoading,
    isAuthenticated,
    canAdd,
  });
  // Tombol "Order Melalui Mitra Terdekat" — gerbangnya di helper murni
  // (`showPartnerProductCta`, diuji) karena aturannya empat lapis dan salah
  // satunya fail-closed; alasan tiap lapis ada di sana. Beda dengan tombol WA
  // tamu di atas: TIDAK digerbangi `canAdd` — justru saat PO tutup atau stok
  // toko habis, mitra terdekat yang masih menyimpan barangnya berguna.
  const showPartnerCta = showPartnerProductCta({
    catalogMode: Boolean(catalogMode),
    settingEnabled: partnerCtaEnabled,
    customerLoading,
    isAuthenticated,
  });
  // Setelah masuk, pembeli dikembalikan ke halaman produk yang sedang dilihat
  // (pola `loginHref` CartCheckout) — bukan ke beranda, supaya ia bisa langsung
  // memesan produk yang tadi dibukanya.
  const guestLoginHref = `/login?next=${encodeURIComponent(pathname)}`;
  // Varian dialog "Masuk untuk memesan": pilihan varian + jumlah dititipkan di
  // `next` supaya selamat dari reload penuh sesudah login (dipulihkan server
  // lewat `parseSelectionQuery` → prop `initialSelection`). `safeInternalPath`
  // menerima query string.
  const loginHrefKeepingSelection = `/login?next=${encodeURIComponent(
    `${pathname}${variantsLazy ? buildSelectionSearch(selectedOptionIds, qty) : ""}`
  )}`;


  return (
    <div className="min-h-screen bg-white pb-28">
      {/* Floating header */}
      <header className="fixed left-1/2 top-0 z-30 flex w-full max-w-md -translate-x-1/2 items-center justify-between px-3 py-3">
        <Link
          href={catalogMode ? buildCatalogPath(catalogMode.kode) : "/"}
          aria-label="Kembali"
          // Kembali ke ENTRI RIWAYAT sebelumnya bila memang milik situs ini —
          // beranda lalu memulihkan tab, jumlah kartu & posisi scroll
          // (useHomeFeedMemory) — alih-alih mendorong "/" baru yang selalu
          // mulai dari atas dan menumpuk entri riwayat. Deep-link (dibuka dari
          // WhatsApp/iklan) tak punya entri di belakang → tetap ke href.
          onClick={(event) => {
            if (!isPlainClick(event) || !canGoBackInApp()) return;
            event.preventDefault();
            router.back();
          }}
          className="grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink shadow-sm backdrop-blur"
        >
          <ArrowLeft size={20} />
        </Link>
        {/* Cart badge — scroll ke keranjang inline (yang merender semua grup
            produk) + total qty lintas produk. Lonceng notifikasi di kirinya
            (idiom header toko).
            Mode katalog: tanpa keranjang & lonceng sama sekali (white-label,
            order via WA reseller). */}
        {catalogMode ? null : (
          <div className="flex items-center gap-2">
            <NotificationBell variant="floating" />
            <button
              type="button"
              aria-label="Lihat keranjang"
              onClick={scrollToCart}
              className="relative grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink shadow-sm backdrop-blur"
            >
              <ShoppingCart size={20} />
              {totalQty > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-shopee px-1 text-[10px] font-bold text-white">
                  {totalQty}
                </span>
              ) : null}
            </button>
          </div>
        )}
      </header>

      {/* Image gallery — swipeable + foto tak terpotong + loading per slide.
          Isinya foto produk DITAMBAH foto per-opsi variasi, dan mengetuk opsi
          ber-foto melompatkan slider ke foto itu (`focusOptionPhoto`).
          Tanpa foto: gambar default = logo toko, KECUALI mode katalog reseller
          (white-label — logo toko induk harus disembunyikan).
          Feed geser: pratinjau produk sebelumnya/berikutnya di kedua ujung. */}
      <ProductImageGallery
        images={gallery.images}
        alt={product.name}
        onActiveChange={setActiveImage}
        storeLogoFallback={!catalogMode}
        controlRef={galleryControl}
        leadingSlide={
          feedData.prev && prevHref ? (
            <NextProductPeek
              step={feedData.prev}
              href={prevHref}
              group={group}
              storeLogoFallback={!catalogMode}
              armed={peekArmed.prev}
              opening={feedOpening === "leading"}
              onOpen={() => openFeed("leading")}
            />
          ) : undefined
        }
        trailingSlide={
          feedData.next && nextHref ? (
            <NextProductPeek
              step={feedData.next}
              href={nextHref}
              group={group}
              storeLogoFallback={!catalogMode}
              armed={peekArmed.next}
              opening={feedOpening === "trailing"}
              onOpen={() => openFeed("trailing")}
            />
          ) : undefined
        }
        onEdgeSettle={openFeed}
        onEdgeIntent={warmFeed}
        edgeLocked={feedOpening !== null}
        trailingHint={feedHint}
        onTrailingHintSeen={markFeedHintSeen}
      />

      {/* Product info */}
      <section className="space-y-4 px-4 py-4">
        {/* Name + status */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold leading-7 text-ink">
              {product.name}
            </h1>
            {/* Merek & Segmen = badge yang bisa diklik → katalog terfilter.
                Mode katalog: filter tetap DI DALAM katalog reseller (bukan
                halaman /merek //kategori toko utama). */}
            {product.brand || product.segment || shipFromWarehouse ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {product.brand ? (
                  <Link
                    href={
                      catalogMode
                        ? buildCatalogMerekPath(catalogMode.kode, {
                            brand: product.brand,
                            type: stockTracked ? "ready_stock" : "preorder",
                          })
                        : `/merek?brand=${encodeURIComponent(product.brand)}&type=${
                            stockTracked ? "ready_stock" : "preorder"
                          }`
                    }
                    className={`inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-shopeeSoft/60 py-1 pr-2.5 text-xs font-bold text-shopee transition active:scale-95 ${
                      brandIconUrl ? "pl-1" : "pl-2.5"
                    }`}
                  >
                    {brandIconUrl ? (
                      // `object-contain` di atas putih: logo merek grosir
                      // lazimnya wordmark lebar berlatar terang — dipotong
                      // `object-cover` namanya ikut hilang.
                      <span className="relative h-5 w-5 shrink-0 overflow-hidden rounded-full bg-white ring-1 ring-shopee/20">
                        <StoreImage
                          src={brandIconUrl}
                          // Nama merek sudah jadi teks badge di sebelahnya.
                          alt=""
                          fill
                          sizes="20px"
                          className="object-contain p-px"
                        />
                      </span>
                    ) : (
                      <Tag size={12} />
                    )}
                    {product.brand}
                  </Link>
                ) : null}
                {product.brand && brandTelegramHref ? (
                  <a
                    href={brandTelegramHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Materi iklan ${product.brand} di Telegram`}
                    className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-600 transition active:scale-95"
                  >
                    <TelegramIcon className="h-3 w-3 shrink-0" />
                    Materi iklan
                  </a>
                ) : null}
                {product.segment ? (
                  <Link
                    href={
                      catalogMode
                        ? buildCatalogKategoriPath(catalogMode.kode, {
                            segment: product.segment,
                            type: stockTracked ? "ready_stock" : "preorder",
                          })
                        : `/kategori?segment=${encodeURIComponent(product.segment)}&type=${
                            stockTracked ? "ready_stock" : "preorder"
                          }`
                    }
                    className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-100 px-2.5 py-1 text-xs font-bold text-ink transition active:scale-95"
                  >
                    <UsersRound size={12} />
                    {product.segment}
                  </Link>
                ) : null}
                {shipFromWarehouse ? (
                  // Bukan tautan: tak ada halaman per gudang. Warna teal netral —
                  // bukan warna primer tema — supaya tak terbaca sebagai promo.
                  <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-700">
                    <Warehouse size={12} aria-hidden="true" />
                    Dikirim dari {shipFromWarehouse}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <StatusBadge
            status={product.status}
            label={product.status === "READY LENGKAP" ? "Ready Stock" : undefined}
          />
        </div>

        {/* Price */}
        <div className="space-y-1.5">
          {hidePrice ? (
            priceEstimate !== undefined ? (
              <p className="text-xl font-extrabold text-ink">{priceEstimateLabel(priceEstimate)}</p>
            ) : null
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <p className={`text-2xl font-extrabold ${selectedPrice === undefined ? "text-muted" : "text-shopee"}`}>
                {formatPrice(selectedPrice)}
              </p>
              {discountBadge && strikePrice !== undefined ? (
                <>
                  <span className="text-sm font-semibold text-muted line-through">
                    {formatPrice(strikePrice)}
                  </span>
                  <span className="rounded-full bg-shopee/10 px-2 py-0.5 text-[11px] font-extrabold text-shopee">
                    {discountBadge}
                  </span>
                </>
              ) : null}
            </div>
          )}
          {extraNote && !hidePrice ? (
            <p className="text-[11px] font-semibold text-muted">{extraNote}</p>
          ) : null}
          {estimateExtraNote && hidePrice ? (
            <p className="text-[11px] font-semibold text-muted">{estimateExtraNote}</p>
          ) : null}
          {priceChain && !hidePrice ? (
            // Rantai harga promo coret (R8): satu baris supaya "10% + 30%"
            // terbaca sebagai angka, bukan teka-teki.
            <p className="text-[11px] font-semibold text-muted">
              Harga umum {formatPrice(priceChain.umum)} → promo −{discountCutLabel(priceChain.promo)} →{" "}
              {formatPrice(priceChain.coret)} → {group?.name ?? "level"} −{discountCutLabel(priceChain.level)} →{" "}
              {priceChain.extra > 0 ? (
                <>
                  {formatPrice(priceChain.akhir - priceChain.extra)} + tambahan {formatPrice(priceChain.extra)} →{" "}
                </>
              ) : null}
              <span className="font-extrabold text-shopee">{formatPrice(priceChain.akhir)}</span>
            </p>
          ) : null}
          {activePromo && !hidePrice ? (
            <div className="flex flex-wrap items-center gap-2">
              {/* Di toko utama chip ini jadi PINTU ke halaman Promo — pembeli
                  yang tertarik pada satu barang diskon hampir selalu mau
                  melihat apa lagi yang ikut kampanye itu. Di mode katalog
                  reseller ia tetap teks biasa: `/promo` milik toko utama, dan
                  navigasi di katalog white-label tak boleh keluar dari
                  `/toko/[kode]`. */}
              {catalogMode ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-shopee px-2 py-0.5 text-[11px] font-extrabold text-white">
                  🔥 {activePromo.name}
                </span>
              ) : (
                <Link
                  href={`/promo?kampanye=${encodeURIComponent(activePromo.campaignId)}`}
                  prefetch={false}
                  className="inline-flex items-center gap-1 rounded-full bg-shopee px-2 py-0.5 text-[11px] font-extrabold text-white"
                >
                  🔥 {activePromo.name} ›
                </Link>
              )}
              {promoTimeLeft ? (
                // Sisa waktu kampanye (R9) — kata-katanya dari helper yang sama
                // dengan chip kartu, supaya dua layar tak bicara dua gaya.
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                  ⏳ {promoTimeLeft}
                </span>
              ) : null}
              {promoLeftLabel ? (
                // Sisa kuota (mig 6020). Merah, bukan kuning: ia habis karena
                // pembeli LAIN, jadi ia mendesak dengan cara berbeda dari
                // tenggat waktu yang bisa dihitung sendiri.
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">
                  {promoLeftLabel}
                </span>
              ) : null}
            </div>
          ) : null}
          {(badgeLabel && !hidePrice) || dpBadgeLabel ? (
            <div className="flex flex-wrap items-center gap-2">
              {badgeLabel && !hidePrice ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                  {badgeLabel}
                </span>
              ) : null}
              {dpBadgeLabel ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                  💰 {dpBadgeLabel}
                </span>
              ) : null}
            </div>
          ) : null}
          {pointHints.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {pointHints.slice(0, 2).map((h) => (
                <span
                  key={h}
                  className="inline-flex items-center gap-1 rounded-full bg-leaf/10 px-2 py-0.5 text-[11px] font-bold text-leaf"
                >
                  🎁 {h}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {showPriceFollowsNote ? (
          <p className="text-xs font-semibold text-muted">
            {priceEstimate !== undefined
              ? PRICE_ESTIMATE_NOTE
              : "Harga menyusul — pesanan tetap bisa dibuat sekarang."}
          </p>
        ) : null}

        {/* Bagikan ke WhatsApp */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={shareToWhatsApp}
            aria-label="Bagikan produk ke WhatsApp"
            className="inline-flex h-10 items-center gap-2 rounded-full bg-[#25D366] px-4 text-sm font-extrabold text-white shadow-soft transition active:scale-95"
          >
            <Share2 size={16} />
            Bagikan ke WA
          </button>
          {/* Simpan ❤ (mig 6270) — tak ada di katalog reseller (white-label:
              pembeli akhir reseller bukan akun toko ini). */}
          {catalogMode ? null : <SaveProductButton productId={product.id} variant="pill" />}
          {/* #1 Bagikan versi Katalog Saya — hanya di toko utama utk reseller
              aktif yang login (punya catalog_slug). Link menuju katalog reseller
              → pembeli akhir order ke dia (dropship). */}
          {!catalogMode && myCatalogSlug ? (
            <button
              type="button"
              onClick={shareMyCatalog}
              aria-label="Bagikan produk versi Katalog Saya"
              className="inline-flex h-10 items-center gap-2 rounded-full bg-shopee px-4 text-sm font-extrabold text-white shadow-soft transition active:scale-95"
            >
              <Store size={16} />
              Bagikan Katalog Saya
            </button>
          ) : null}
          {/* #2 Order Melalui Mitra Terdekat (mig 4230, fitur B) — OPSIONAL,
              dinyalakan admin dari Pengaturan Website, dan HANYA untuk
              pengunjung yang belum login. Gerbang lengkapnya:
              `showPartnerProductCta` di partnerDirectory.ts. */}
          {showPartnerCta ? (
            <a
              href={partnerDirectoryPath(null, product.slug)}
              aria-label="Order produk ini melalui mitra terdekat"
              className="inline-flex h-10 items-center gap-2 rounded-full border-2 border-shopee px-4 text-sm font-extrabold text-shopee transition active:scale-95"
            >
              <MapPin size={16} />
              Order Melalui Mitra Terdekat
            </a>
          ) : null}
          <button
            type="button"
            onClick={sharePhoto}
            disabled={sharingPhoto}
            aria-label="Bagikan foto produk ke WhatsApp Status"
            className="inline-flex h-10 items-center gap-2 rounded-full border-2 border-[#25D366] px-4 text-sm font-extrabold text-[#25D366] transition active:scale-95 disabled:opacity-50"
          >
            <Camera size={16} />
            {sharingPhoto ? "Memuat…" : "Buat Status Whatsapp"}
          </button>
        </div>

        {/* Deadline / Estimasi — disembunyikan untuk produk Ready Stock */}
        {!stockTracked ? (
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-neutral-100 bg-shell p-3">
              <p className="text-[11px] font-bold uppercase text-muted">Deadline PO</p>
              <p className="mt-1 text-sm font-extrabold text-ink">
                {product.deadline || "—"}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-100 bg-shell p-3">
              <p className="text-[11px] font-bold uppercase text-muted">Estimasi</p>
              <p className="mt-1 text-sm font-extrabold text-ink">
                {product.estimatedReady || "—"}
              </p>
            </div>
          </div>
        ) : null}

        {/* Reseller / Agen discount — disembunyikan di mode katalog (info tier
            diskon internal toko tak boleh bocor ke pembeli akhir). */}
        {(product.resellerDiscount || product.agentDiscount) && !catalogMode ? (
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-shopeeSoft/60 p-3">
              <p className="text-[11px] font-bold uppercase text-shopee">Reseller</p>
              <p className="mt-1 text-sm font-bold text-ink">
                {product.resellerDiscount ?? "-"}
              </p>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3">
              <p className="text-[11px] font-bold uppercase text-leaf">Agen</p>
              <p className="mt-1 text-sm font-bold text-ink">
                {product.agentDiscount ?? "-"}
              </p>
            </div>
          </div>
        ) : null}

        {/* ─── Lebih hemat dalam bundel (mig 8040) ───────────────────────
            Kartu ringkas per bundel yang memuat produk ini → /bundling/<slug>.
            Harga lengkapnya (terpisah, hemat, tambahan ukuran) dihitung di
            halaman bundel; di sini ringan, tanpa memuat varian komponen lain. */}
        {myBundles.length > 0 ? (
          <div className="space-y-2">
            <h2 className="text-sm font-extrabold text-ink">Lebih hemat dalam bundling</h2>
            {myBundles.slice(0, 3).map((card) => (
              <BundleCard key={card.id} card={card} variant="compact" productId={product.id} />
            ))}
            {myBundles.length > 3 ? (
              <Link href="/bundling" prefetch={false} className="block text-xs font-extrabold text-shopee">
                Lihat semua bundling ›
              </Link>
            ) : null}
          </div>
        ) : null}

        {/* ─── Gratis hadiah (hadiah pembelian, mig 8680) ────────────────
            Aturan "beli X gratis Y" yang syaratnya produk ini. Tak pernah di
            katalog reseller (white-label), idiom blok bundel. */}
        {!catalogMode ? (
          <ProductGiftBlock
            product={{
              id: product.id,
              brand: product.brand,
              category: product.category,
              productType: product.productType ?? null
            }}
            group={rawGroup}
          />
        ) : null}

        {/* ─── Variasi ────────────────────────────────── */}
        <div className="border-t border-neutral-100 pt-4">
          {/* Judul HANYA untuk produk yang benar-benar punya pilihan variasi.
              Stepper qty + Panduan Ukuran turun ke atas tombol Keranjang
              (putusan pemilik 2026-09-16), dan labelnya IKUT turun bersamanya:
              judul yang dulu berbunyi "Jumlah" di sini akan menggantung tanpa
              apa pun di sampingnya pada produk tanpa variasi. */}
          {hasVariationChoice ? (
            <h2 className="mb-3 text-sm font-extrabold text-ink">Variasi</h2>
          ) : null}

          {/* Info stok varian terpilih — hanya untuk produk ready stock */}
          {stockTracked ? (
            <p
              className={`mb-3 text-xs font-bold ${
                selectedInStock ? "text-leaf" : "text-shopee"
              }`}
            >
              {selectedInStock
                ? `Stok tersedia: ${selectedStockQty} pcs`
                : "Stok varian ini habis"}
            </p>
          ) : null}

          {/* Sisa kuota PO — SELALU tampil untuk produk PO berkuota. Nadanya
              naik sendiri saat menipis/habis; `null` berarti produk ini PO
              biasa (tanpa jatah) atau variannya belum termuat. */}
          {quotaLine ? (
            <p className={`mb-3 text-xs font-bold ${QUOTA_TONE_CLASS[quotaLine.tone]}`}>
              {quotaLine.label}
            </p>
          ) : null}

          {product.variantGroups.length > 0 ? (
            /* ── Mode grup: tampilkan per grup (Warna, Ukuran, dll) ── */
            <VariantGroupSelector
              groups={product.variantGroups}
              variations={product.variations}
              showPriceExtra={!hidePrice}
              selected={selectedVariation}
              onSelect={setSelectedVariation}
              stockTracked={stockTracked}
              quota={poLimited ? { limited: true, used: quotaUsed } : undefined}
              lazy={variantsLazy}
              blockedCombos={blockedComboSet}
              onVariantShift={setVariantShift}
              selectedOptionIds={selectedOptionIds}
              onSelectOption={(optionId) => {
                // Mengetuk opsi yang kombinasinya dimatikan admin TIDAK boleh
                // berakhir di pintu tertutup: grup lain digeser ke kombinasi
                // hidup terdekat yang tetap memuat opsi itu, dan pergeserannya
                // dikatakan. Tanpa ini "Navara famset" mengunci Pashmina
                // selamanya (butuh Variasi DAN Size berubah bersamaan).
                const next = resolveSelectionForOption(
                  product.variantGroups,
                  selectedOptionIds,
                  optionId,
                  blockedComboSet
                );
                const clicked = product.variantGroups.find((g) =>
                  g.options.some((o) => o.id === optionId)
                );
                setVariantShift(
                  describeVariantShift(
                    product.variantGroups,
                    selectedOptionIds,
                    next,
                    clicked?.id ?? ""
                  )
                );
                setSelectedOptionIds(next);
              }}
              onOptionPicked={focusOptionPhoto}
            />
          ) : product.variations.length > 1 ? (
            /* ── Fallback flat list (produk lama tanpa sistem grup, >1 varian) ── */
            <div className="grid grid-cols-2 gap-2">
              {product.variations.map((variation) => {
                const isSelected = selectedVariation?.id === variation.id;
                const inStock = isVariationAvailable(product, variation, quotaUsed);
                return (
                  <button
                    key={variation.id}
                    type="button"
                    disabled={!inStock}
                    onClick={() => {
                      if (inStock) setSelectedVariation(variation);
                    }}
                    className={`min-h-11 rounded-xl border px-3 text-sm font-bold transition ${
                      isSelected
                        ? "border-shopee bg-shopee text-white"
                        : inStock
                          ? "border-neutral-100 bg-white text-ink active:bg-shopeeSoft/60"
                          : "cursor-not-allowed border-zinc-200 bg-zinc-200/70 text-zinc-400 line-through"
                    }`}
                  >
                    {variation.name}
                  </button>
                );
              })}
            </div>
          ) : (
            /* ── 1 varian default (produk "harga saja") → tanpa selektor. ── */
            null
          )}

          {/* Grup lain yang ikut bergeser karena kombinasi yang diminta tidak
              tersedia. Pergeseran itu SENGAJA (mengklik warna yang ukurannya
              habis mendarat di ukuran yang masih terbuka, bukan kombinasi
              mati) — yang tidak boleh adalah pergeseran yang TANPA SUARA:
              pembeli yang mengetuk satu opsi lalu langsung menekan Tambah ke
              Keranjang bisa memesan warna yang tak pernah ia pilih. */}
          {variantShift.length > 0 ? (
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-semibold leading-5 text-amber-900">
              {variantShift
                .map((s) => `${s.groupName} disesuaikan dari ${s.from} ke ${s.to}`)
                .join(" · ")}{" "}
              — kombinasi sebelumnya tidak tersedia.
            </p>
          ) : null}

          {/* Kombinasi yang tidak dijual — dikatakan TERUS TERANG di tempat
              pembeli memilihnya, bukan disimpan sampai checkout. Tombol
              opsinya sudah dicoret; kalimat ini menjelaskan coretannya. */}
          {variantUnavailable ? (
            <p className="mt-4 rounded-xl bg-zinc-100 px-3 py-2 text-[11px] font-semibold leading-5 text-zinc-600">
              Variasi ini tidak dijual. Silakan pilih kombinasi lain — yang
              dicoret berarti tidak tersedia.
            </p>
          ) : null}

          {/* Varian yang harganya belum disetel admin. Ditulis TERUS TERANG,
              bukan disamarkan jadi "stok habis" dan bukan pula ditutup dengan
              angka varian lain (natasyashop #2095): barangnya ada dan boleh
              dipesan, harganya yang menyusul dan dikonfirmasi admin di nota. */}
          {priceNotSet && priceEstimate === undefined ? (
            <p className="mt-4 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-semibold leading-5 text-amber-900">
              Harga varian ini belum disetel. Pesanan tetap bisa dibuat sekarang —
              harganya menyusul dan akan dikonfirmasi admin sebelum pembayaran.
            </p>
          ) : null}

          {/* ─── Jumlah + Panduan Ukuran ──────────────────────────────────
              Duduk TEPAT di atas tombol Keranjang (putusan pemilik
              2026-09-16): pembeli menetapkan variasinya dulu, lalu jumlahnya,
              lalu menekan tombol — urutan yang sama dengan arah bacanya.
              Sengaja SESUDAH ketiga catatan varian di atas supaya catatan itu
              tetap menempel pada pemilih yang dijelaskannya, bukan terdorong
              menjauh oleh stepper.
              `SizeGuideButton` merender dirinya jadi `null` bila admin belum
              menunjuk foto panduan ukuran (mig 5890), jadi baris ini menyusut
              jadi label + stepper saja tanpa celah menganga. */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <h2 className="min-w-0 truncate text-sm font-extrabold text-ink">
              Jumlah
            </h2>
            <div className="flex shrink-0 items-center gap-2">
              <SizeGuideButton
                imageUrl={product.sizeGuideImage}
                productName={product.name}
              />
              <QuantityStepper value={qty} onChange={setQty} max={maxQty} />
            </div>
          </div>

          {/* ─── Tambah ke Keranjang (mode katalog) ───────────────────────
              SATU jalur saja. Tombol "Order via WhatsApp" satu-produk yang ada
              sejak mig 216 DIHAPUS (putusan pemilik 2026-09-11): keranjang
              menggantikannya seutuhnya dan sanggup mengirim satu produk sama
              baiknya, sementara dua tombol berdampingan menuntut pembeli memilih
              antara dua jalan yang berakhir di WhatsApp yang sama.
              TIDAK dirender tanpa provider keranjang — halaman ini mustahil
              sampai ke sana (katalog nonaktif berhenti di `CatalogUnavailable`);
              gerbang sesungguhnya ada di dalam `addToCatalogCart`. */}
          {catalogMode ? (
            <button
              type="button"
              onClick={addToCatalogCart}
              disabled={!canAdd}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99] disabled:bg-zinc-300"
            >
              <ShoppingCart size={17} />
              {!canOrder
                ? "PO Ditutup"
                : !selectedInStock
                  ? "Stok Habis"
                  : selectedQuotaExhausted
                    ? "Kuota Habis"
                    : variantUnavailable
                      ? "Variasi Tidak Dijual"
                      : "Tambah ke Keranjang"}
            </button>
          ) : (
            <button
              type="button"
              onClick={addToCart}
              disabled={!canAdd}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99] disabled:bg-zinc-300"
            >
              <ShoppingCart size={17} />
              {!canOrder
                ? "PO Ditutup"
                : !selectedInStock
                  ? "Stok Habis"
                  : selectedQuotaExhausted
                    ? "Kuota Habis"
                    : variantUnavailable
                      ? "Variasi Tidak Dijual"
                      : resolvingVariant
                        ? "Memuat varian…"
                        : "Tambah ke Keranjang"}
            </button>
          )}

          {/* ── Order via WhatsApp (TAMU / belum login) ──────────────────
              Checkout wajib login, jadi pengunjung tanpa akun tetap punya
              jalur pesan: chat ke nomor "Hubungi Admin" (Pengaturan Website).
              Bisa dimatikan pemilik toko di kartu yang sama dengan nomor itu;
              saat mati, pengunjung tamu kembali hanya punya tautan "Masuk". */}
          {showGuestWaCta ? (
            <>
              {/* Keterangan sebelum tombol: pengunjung perlu tahu MENGAPA
                  pesanannya keluar dari aplikasi (checkout menuntut login) dan
                  apa untungnya membuat akun (harga grup reseller). */}
              <p className="mt-3 rounded-xl bg-shopeeSoft/60 px-3 py-2 text-[11px] font-semibold leading-5 text-ink">
                Kamu belum login, jadi pesanan dialihkan ke WhatsApp admin.{" "}
                <Link
                  href={guestLoginHref}
                  className="font-extrabold text-shopee underline"
                >
                  Masuk
                </Link>{" "}
                untuk mendapatkan harga spesial.
              </p>
              <button
                type="button"
                onClick={orderViaWhatsAppAsGuest}
                className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99]"
              >
                <MessageCircle size={17} />
                Order via WhatsApp
              </button>
            </>
          ) : null}
        </div>

        {/* ─── Keranjang section (tanpa checkout di mode katalog) ─────── */}
        {catalogMode ? null : (
          <div ref={cartRef} className="border-t border-neutral-100 pt-2">
            <CartCheckout mode="inline" canOrder={canOrder} />
          </div>
        )}

        {/* ─── Deskripsi (diletakkan di bawah keranjang) ─ */}
        {product.description ? (
          <div className="border-t border-neutral-100 pt-4">
            <h2 className="text-sm font-extrabold text-ink">Deskripsi</h2>
            <p
              ref={descRef}
              className={`mt-2 whitespace-pre-line text-sm leading-6 text-muted ${
                descExpanded ? "" : "line-clamp-3"
              }`}
            >
              {product.description}
            </p>
            {descOverflows || descExpanded ? (
              <button
                type="button"
                onClick={() => setDescExpanded((open) => !open)}
                className="mt-2 text-xs font-extrabold text-shopee"
              >
                {descExpanded ? "Sembunyikan" : "Tampilkan selengkapnya"}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* Kombinasi malas gagal disiapkan: minta MASUK (sesi) atau COBA LAGI
          (server). Bukan dialog keranjang — keranjangnya bisa saja kosong. */}
      {variantBlock ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/35 px-4 pb-4">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-soft">
            <h2 className="text-base font-extrabold text-ink">
              {variantBlock.reason === "LOGIN_REQUIRED"
                ? "Masuk untuk memesan produk PO ini"
                : variantBlock.reason === "REJECTED"
                  ? "Kombinasi ini tidak bisa dipesan"
                  : "Varian belum bisa disiapkan"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted">
              {variantBlock.reason === "LOGIN_REQUIRED"
                ? "Produk PO ini hanya bisa dipesan lewat akun. Masuk dulu. Pilihan varian dan jumlahmu tetap tersimpan, dan kamu kembali ke halaman ini setelah masuk. Belum punya akun? Pesan lewat tombol Order via WhatsApp di bawah."
                : variantBlock.reason === "REJECTED"
                  ? (variantBlock.message ||
                    "Kombinasi varian yang kamu pilih tidak bisa dipesan di produk ini.")
                  : "Kombinasi varian yang kamu pilih belum bisa disiapkan, biasanya karena koneksi. Coba lagi sebentar lagi. Kalau masih gagal, hubungi admin."}
            </p>
            {variantBlock.reason === "REJECTED" ? (
              <button
                type="button"
                onClick={() => setVariantBlock(null)}
                className="mt-4 h-11 w-full rounded-lg bg-shopee text-sm font-bold text-white"
              >
                Tutup
              </button>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setVariantBlock(null)}
                  className="h-11 rounded-lg border border-neutral-100 text-sm font-bold text-ink"
                >
                  {variantBlock.reason === "LOGIN_REQUIRED" ? "Nanti" : "Tutup"}
                </button>
                {variantBlock.reason === "LOGIN_REQUIRED" ? (
                  <Link
                    href={loginHrefKeepingSelection}
                    className="flex h-11 items-center justify-center rounded-lg bg-shopee text-sm font-bold text-white"
                  >
                    Masuk
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setVariantBlock(null);
                      void addToCart();
                    }}
                    className="h-11 rounded-lg bg-shopee text-sm font-bold text-white"
                  >
                    Coba lagi
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Added toast — bug H6-13: dulu bottom-8 z-40 tertutup total BottomNav
          (z-40 juga, tapi dirender lebih akhir di layout jadi menang paint
          order) di SEMUA perangkat. Dinaikkan di atas BottomNav + z-50 supaya
          selalu menang; `toast-above-nav` (globals.css) menjaga jarak di
          perangkat notch lewat POSISI — `safe-bottom` dulu ikut dicat gelap
          dan menutupi ikon BottomNav di iPhone PWA. `w-max`: elemen fixed
          ber-`left-1/2` hanya diberi separuh layar untuk menata isinya, jadi
          tanpa ini pesannya patah baris di layar 390px. */}
      {showAdded ? (
        <div className="toast-above-nav fixed left-1/2 z-50 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-full bg-zinc-900 px-4 py-2 text-center text-xs font-bold text-white shadow-soft">
          ✓ Ditambahkan ke keranjang
        </div>
      ) : null}

      {/* Toast keranjang katalog — padanan H6-13 di atas. */}
      {catalogCartMsg ? (
        <div className="toast-above-nav fixed left-1/2 z-50 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-full bg-zinc-900 px-4 py-2 text-center text-xs font-bold text-white shadow-soft">
          {catalogCartMsg}
        </div>
      ) : null}

      {/* Photo share toast — padanan H6-13 di atas. */}
      {photoShareMsg ? (
        <div className="toast-above-nav fixed left-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-2xl bg-zinc-900 px-4 py-3 text-center text-xs font-bold text-white shadow-soft">
          {photoShareMsg}
        </div>
      ) : null}
    </div>
  );
}
